// SimplifyReminderModal.jsx - Reminder modal extracted from Simplify.jsx
import React, { useState } from "react";
import './ModalComponents.css';
import { Capacitor } from "@capacitor/core";
import { API_BASE } from "../../config/api";
import { httpRequest } from "../../utils/httpClient";
import { authService } from "../../services/auth";
import { getTimeZoneForCountry, zonedLocalToUtcISO } from "../../utils/timezone";
import logger from '../../utils/logger';

/**
 * SimplifyReminderModal - Modal for setting game reminders
 */
function SimplifyReminderModal({ gameId, gameName, onClose, userCountry, showSuccessBannerMessage, showTransientBanner }) {
  const [title, setTitle] = useState("");
  const [actionKeyword, setActionKeyword] = useState("Explore");
  const [date, setDate] = useState("");
  const [time, setTime] = useState("12:00");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Check if we're on mobile app
    const isNativeMobile = Capacitor.isNativePlatform();

    if (isNativeMobile) {
      // On mobile app, skip browser notification checks and proceed directly
      logger.debug('Mobile app detected - skipping browser notification checks');
    } else {
      // Browser notification checks for web
      if (!('Notification' in window)) {
        showTransientBanner('Your browser does not support notifications. Reminders may not work properly.');
        setIsSubmitting(false);
        return;
      }

      // Additional check for mobile browser compatibility
      const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
      const isStandalone = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone;

      if (isMobile && !isStandalone) {
        // On mobile browsers, notifications might not work properly
        logger.warn('Mobile browser detected - notifications may have limited functionality');
      }

      // Check current permission status
      let permission = Notification.permission;

      // If permission is not granted, request it
      if (permission === 'default') {
        try {
          permission = await Notification.requestPermission();
        } catch (error) {
          logger.error('Error requesting notification permission:', error);
          // On mobile, permission requests might fail silently
          if (isMobile) {
            showTransientBanner('Mobile browser detected. For best reminder experience, consider:\n\n' +
                  '• Installing this app as a PWA (add to home screen)\n' +
                  '• Using a desktop browser\n' +
                  '• Ensuring browser notifications are enabled in settings');
          } else {
            showTransientBanner('Unable to request notification permission. Please check your browser settings.');
          }
          setIsSubmitting(false);
          return;
        }
      }

      // If permission is still not granted, show helpful message
      if (permission !== 'granted') {
        let message = `Notifications are currently blocked. To receive reminders:\n\n` +
          `1. Click the lock/icon 🔒 in your address bar\n` +
          `2. Find "Notifications" in the permissions\n` +
          `3. Change from "Block" to "Allow"\n` +
          `4. Refresh this page and try again`;

        if (isMobile) {
          message += `\n\nMobile users: For best results, install this app as a PWA (add to home screen) or use a desktop browser.`;
        }

        showTransientBanner(message);
        setIsSubmitting(false);
        return;
      }
    }

    // Check if user is authenticated
    const authHeaders = authService.getAuthHeaders();
    if (!authHeaders.Authorization) {
      showTransientBanner("Please log in to set reminders.");
      setIsSubmitting(false);
      return;
    }

    try {
      // Create proper datetime string like Curate does
      const timeZone = getTimeZoneForCountry(userCountry);
      const datetime = zonedLocalToUtcISO(date, time, timeZone);

      const res = await httpRequest(`${API_BASE}/reminders`, {
        method: "POST",
        headers: {
          ...authHeaders,
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          game_id: gameId,
          title: title || `${actionKeyword} ${gameName}`,
          action_keyword: actionKeyword,
          reminder_datetime: datetime,
        })
      });

      if (res.ok) {
        showSuccessBannerMessage(`Reminder set for "${gameName}"`);
        onClose();
      } else {
        const error = await res.text();
        showTransientBanner(`Failed to set reminder: ${error}`);
      }
    } catch (error) {
      showTransientBanner("Network error. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="safe-area-overlay" style={{
      position: "fixed",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: "rgba(0, 0, 0, 0.5)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      zIndex: 1000
    }}>
      <div style={{
        backgroundColor: "var(--bg-primary, white)",
        padding: "2rem",
        borderRadius: "8px",
        maxWidth: "500px",
        width: "90%",
        maxHeight: "80dvh",
        overflowY: "auto",
        color: "var(--text-primary, #333)"
      }}>
        <h2 style={{ color: "var(--text-primary, #333)" }}>Set Reminder</h2>
        <p style={{ color: "var(--text-secondary, #666)" }}>
          Get reminded about &quot;{gameName}&quot;.
        </p>

        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: "1rem" }}>
            <label style={{
              display: "block",
              marginBottom: "0.5rem",
              fontWeight: "bold",
              color: "var(--text-primary, #333)"
            }}>
              Title (optional)
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={`${actionKeyword} ${gameName}`}
              style={{
                width: "100%",
                padding: "0.5rem",
                border: "1px solid var(--border-color, #ddd)",
                borderRadius: "4px",
                backgroundColor: "var(--bg-secondary, white)",
                color: "var(--text-primary, #333)"
              }}
            />
          </div>

          <div style={{ marginBottom: "1rem" }}>
            <label style={{
              display: "block",
              marginBottom: "0.5rem",
              fontWeight: "bold",
              color: "var(--text-primary, #333)"
            }}>
              Action Type
            </label>
            <select
              value={actionKeyword}
              onChange={(e) => setActionKeyword(e.target.value)}
              style={{
                width: "100%",
                padding: "0.5rem",
                border: "1px solid var(--border-color, #ddd)",
                borderRadius: "4px",
                backgroundColor: "var(--bg-secondary, white)",
                color: "var(--text-primary, #333)"
              }}
            >
              <option value="Explore">Explore</option>
              <option value="Buy">Buy</option>
              <option value="Play">Play</option>
              <option value="Research">Research</option>
              <option value="Other">Other</option>
            </select>
          </div>

          <div style={{ marginBottom: "1rem" }}>
            <label style={{
              display: "block",
              marginBottom: "0.5rem",
              fontWeight: "bold",
              color: "var(--text-primary, #333)"
            }}>
              Date
            </label>
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              required
              style={{
                width: "100%",
                padding: "0.5rem",
                border: "1px solid var(--border-color, #ddd)",
                borderRadius: "4px",
                backgroundColor: "var(--bg-secondary, white)",
                color: "var(--text-primary, #333)"
              }}
            />
          </div>

          <div style={{ marginBottom: "1rem" }}>
            <label style={{
              display: "block",
              marginBottom: "0.5rem",
              fontWeight: "bold",
              color: "var(--text-primary, #333)"
            }}>
              Time
            </label>
            <input
              type="time"
              value={time}
              onChange={(e) => setTime(e.target.value)}
              required
              style={{
                width: "100%",
                padding: "0.5rem",
                border: "1px solid var(--border-color, #ddd)",
                borderRadius: "4px",
                backgroundColor: "var(--bg-secondary, white)",
                color: "var(--text-primary, #333)"
              }}
            />
          </div>

          <div style={{ display: "flex", gap: "1rem", justifyContent: "flex-end" }}>
            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
              style={{
                padding: "0.75rem 1.5rem",
                border: "1px solid var(--border-color, #ddd)",
                borderRadius: "4px",
                background: "var(--bg-secondary, white)",
                color: "var(--text-primary, #333)",
                cursor: isSubmitting ? "not-allowed" : "pointer"
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              style={{
                padding: "0.75rem 1.5rem",
                border: "none",
                borderRadius: "4px",
                background: "#1976d2",
                color: "white",
                cursor: isSubmitting ? "not-allowed" : "pointer"
              }}
            >
              {isSubmitting ? "Setting..." : "Schedule"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default SimplifyReminderModal;
